import { Component, OnInit, OnChanges, Input, Output, EventEmitter } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Modal } from '../../model/modal';

import { SitesService } from '../../services/sites.service';
import { CommonService } from '../../services/common.service';
import { BaseComponent } from '@app/directives/base-component';
import { Helper } from 'src/4services/2helper';

@Component({
  selector: 'dealer-partition-arming',
  templateUrl: './partition-arming.component.html',
  styleUrls: ['../common.scss', './partition-arming.component.scss'],
  standalone: false
})
export class PartitionArmingComponent extends BaseComponent implements OnInit, OnChanges {

  @Input() modal: Modal;
  @Input() active: boolean;
  @Output() event: EventEmitter<any> = new EventEmitter();
  @Output() partitionUpdated: EventEmitter<{toArm: number[], toDisarm: number[]}> = new EventEmitter();

  @Input() site: any;
  @Input() me: any;
  @Input() partitions: any[] = [];

  m_warning = new Modal();
  selectedPartitionIds: number[] = [];
  partitionStates: Map<number, number> = new Map();
  syncingPartitionIds: Set<number> = new Set();

  currentStep: number = 1;
  pendingSiteAction: number | null = null; // 1=ARM, 2=DISARM
  step2SelectedIds: number[] = [];

  constructor(
    private sitesService: SitesService,
    private commonService: CommonService,
    private helper: Helper,
  ) {
    super();
  }

  ngOnInit() {
    this.sitesService.onOpenPartitionArming.pipe(takeUntil(this.destroy$)).subscribe(res => {
      setTimeout(() => {
        this.openPartitionArmingModal();
      }, 1000);
    });
    this.initPartitionStates();
  }

  ngOnChanges(changes) {
    if (changes.partitions) {
      this.syncingPartitionIds.clear();
      this.initPartitionStates();
    }
    if (changes.active && changes.active.currentValue === true) {
      this.initPartitionStates();
    }
  }

  openPartitionArmingModal() {
    if (this.modal) {
      this.modal.open();
    }
  }

  initPartitionStates() {
    this.partitions = this.partitions.sort((a, b) => a.partition_number - b.partition_number);
    this.partitionStates.clear();
    this.partitions.forEach(partition => {
      this.partitionStates.set(partition.partition_id, partition.arming_state);
    });
  }

  canChangePartitionArmingStatus() {
    const isDealerMatch = this.me.dealer_id === this.site.dealer_id;
    const isPartnerMatch = this.me.type === 2 && this.site.dealer_type === 3 && this.me.dealer_id === this.site.partner_id;
    const hasPermission = this.helper.permission.has("dealer_site_arming_mode_update");
    return (isDealerMatch || isPartnerMatch) && hasPermission;
  }

  isUnknownPartition(partition) {
    return partition?.arming_state !== 1 && partition?.arming_state !== 2;
  }

  isReadonlyPartition(partition) {
    const isPanelArmingPartition = partition.arming_source === 2;
    const isADCPartition = partition.arming_source === 4;
    const isOEVirtualPanel = this.site?.exp_site_info?.filter(info => info.registered_by_partner?.toLowerCase() === 'oe').every(info => info.arm_state_provider === 'VIRTUAL_PANEL');
    const isNotSupportOEPartition = partition.arming_source === 5 && !isOEVirtualPanel;

    return isPanelArmingPartition || isADCPartition || isNotSupportOEPartition;
  }

  getPartitionStatus(partition) {
    if (this.isUnknownPartition(partition)) return 'UNKNOWN';
    const armingAction = this.partitionStates.get(partition.partition_id);
    return armingAction === 2 ? 'DISARMED' : 'ARMED';
  }

  getPartitionStatusColor(partition) {
    const status = this.getPartitionStatus(partition);
    if (status === 'UNKNOWN') return 'rgba(0, 0, 0, 0.1)';
    return status === 'DISARMED' ? 'rgba(30, 166, 117, 0.25)' : 'rgba(255, 127, 22, 0.25)';
  }

  togglePartitionSelection(partitionId: number) {
    const currentAction = this.partitionStates.get(partitionId);
    const newAction = currentAction === 1 ? 2 : 1; // 1 <-> 2 toggle
    this.partitionStates.set(partitionId, newAction);
  }

  isSyncing(partitionId: number): boolean {
    return this.syncingPartitionIds.has(partitionId);
  }

  hasAnySyncing(): boolean {
    return this.syncingPartitionIds.size > 0;
  }

  onPartitionClick(partition: any) {
    if (this.hasAnySyncing()) return;

    const currentStatus = this.getPartitionStatus(partition);
    const nextAction = currentStatus === 'ARMED' ? 'DISARM' : 'ARM';
    const nextArmingAction = nextAction === 'ARM' ? 1 : 2;
    const partitionLabel = partition.partition_name || `Partition ${partition.partition_number}`;
    const msg = `Do you want to <b>${nextAction}</b> <b>${partitionLabel}</b>?`;

    this.confirmDialog('Confirm', msg, 'warning', 'orange');
    this.m_warning.data['submit_func'] = () => {
      const ids = [partition.partition_id];
      this.syncingPartitionIds.add(partition.partition_id);
      this.updatePartitionArmingStatus(ids, nextArmingAction)
        .then(() => {
          this.m_warning.close();
          this.partitionUpdated.emit({
            toArm: nextArmingAction === 1 ? ids : [],
            toDisarm: nextArmingAction === 2 ? ids : [],
          });
        })
        .catch(err => {
          this.syncingPartitionIds.delete(partition.partition_id);
          this.m_warning.close();
          setTimeout(() => this.errorDialog(err, 'Update Partition Arming'), 200);
        });
    };
  }

  hasChanges() {
    for (const partition of this.partitions) {
      const currentState = this.partitionStates.get(partition.partition_id);
      if (currentState !== partition.arming_state) {
        return true;
      }
    }
    return false;
  }

  isPartitionSelected(partitionId: number): boolean {
    return this.selectedPartitionIds.includes(partitionId);
  }

  onPartitionCheckboxChange(partitionId, event) {
    if (event.checked) {
      if (!this.selectedPartitionIds.includes(partitionId)) {
        this.selectedPartitionIds.push(partitionId);
      }
    } else {
      this.selectedPartitionIds = this.selectedPartitionIds.filter(id => id !== partitionId);
    }
  }


  startSiteAction(action: number) {
    if (this.hasAnySyncing()) return;
    this.pendingSiteAction = action;
    this.step2SelectedIds = [];
    this.currentStep = 2;
  }

  backToStep1() {
    this.currentStep = 1;
    this.pendingSiteAction = null;
    this.step2SelectedIds = [];
  }

  isStep2Selected(partitionId: number): boolean {
    return this.step2SelectedIds.includes(partitionId);
  }

  isStep2Disabled(partition: any): boolean {
    if (this.isReadonlyPartition(partition)) return true;
    const status = this.getPartitionStatus(partition);
    if (this.pendingSiteAction === 1 && status === 'ARMED') return true;
    if (this.pendingSiteAction === 2 && status === 'DISARMED') return true;
    return false;
  }

  isAllSelected(): boolean {
    const selectable = this.partitions.filter(p => !this.isStep2Disabled(p));
    return selectable.length > 0 && selectable.every(p => this.step2SelectedIds.includes(p.partition_id));
  }

  toggleSelectAll() {
    const selectable = this.partitions.filter(p => !this.isStep2Disabled(p)).map(p => p.partition_id);
    if (this.isAllSelected()) {
      this.step2SelectedIds = [];
    } else {
      this.step2SelectedIds = [...selectable];
    }
  }

  toggleStep2Selection(partitionId: number) {
    if (this.step2SelectedIds.includes(partitionId)) {
      this.step2SelectedIds = this.step2SelectedIds.filter(id => id !== partitionId);
    } else {
      this.step2SelectedIds.push(partitionId);
    }
  }

  applyStep2() {
    if (this.step2SelectedIds.length === 0 || this.hasAnySyncing()) return;
    const action = this.pendingSiteAction === 1 ? 'ARM' : 'DISARM';
    const count = this.step2SelectedIds.length;
    const msg = `Do you want to <b>${action}</b> <b>${count}</b> partition${count > 1 ? 's' : ''}?`;

    this.confirmDialog('Confirm', msg, 'warning', 'orange');
    this.m_warning.data['submit_func'] = () => {
      const ids = [...this.step2SelectedIds];
      ids.forEach(id => this.syncingPartitionIds.add(id));
      this.updatePartitionArmingStatus(ids, this.pendingSiteAction)
        .then(() => {
          const toArm = this.pendingSiteAction === 1 ? ids : [];
          const toDisarm = this.pendingSiteAction === 2 ? ids : [];
          this.partitionUpdated.emit({ toArm, toDisarm });
          this.m_warning.close();
          this.backToStep1();
        })
        .catch(err => {
          ids.forEach(id => this.syncingPartitionIds.delete(id));
          this.m_warning.close();
          setTimeout(() => this.errorDialog(err, 'Update Partition Arming'), 200);
        });
    };
  }

  changeAllPartitions(armingAction: number) {
    if (this.hasAnySyncing()) return;
    const allPartitionIds = this.partitions
      .filter(p => !this.isUnknownPartition(p) && !this.isReadonlyPartition(p))
      .map(p => p.partition_id);
    if (allPartitionIds.length === 0) return;
    const action = armingAction === 1 ? 'ARM' : 'DISARM';
    const msg = `Do you want to set all partitions of the site to <b>${action}</b>?`;
    this.confirmDialog('Confirm', msg, 'warning', 'orange');
    this.m_warning.data['submit_func'] = () => {
      this.updatePartitionArming(allPartitionIds, armingAction);
    };
  }

  updatePartitionArming(partitionIds: number[], armingAction: number) {
    if (!this.site || !this.me) return;

    partitionIds.forEach(id => this.syncingPartitionIds.add(id));
    const dealerId = this.me.dealer_id;
    const siteId = this.site.site_id;

    this.sitesService.updateSitePartitionArming(dealerId, siteId, partitionIds, armingAction).subscribe(
      res => {
        this.selectedPartitionIds = [];
        const toArm = armingAction === 1 ? partitionIds : [];
        const toDisarm = armingAction === 2 ? partitionIds : [];
        this.partitionUpdated.emit({toArm, toDisarm});
        this.m_warning.close();
        this.modal.close();
      },
      err => {
        partitionIds.forEach(id => this.syncingPartitionIds.delete(id));
        this.m_warning.close();
        setTimeout(() => {
          this.errorDialog(err, 'Update Partition Arming');
        }, 200);
      }
    );
  }

  applyChanges() {
    if (!this.hasChanges() || this.hasAnySyncing()) return;

    const toArm: number[] = [];
    const toDisarm: number[] = [];

    this.partitions.forEach(partition => {
      const currentState = this.partitionStates.get(partition.partition_id);
      const originalState = partition.arming_state;

      if (currentState !== originalState) {
        if (currentState === 1) {
          toArm.push(partition.partition_id);
        } else if (currentState === 2) {
          toDisarm.push(partition.partition_id);
        }
      }
    });

    const totalChanges = toArm.length + toDisarm.length;
    let msg = `Do you want to apply changes to <b>${totalChanges}</b> partition${totalChanges > 1 ? 's' : ''}?`;

    if (toArm.length > 0 && toDisarm.length > 0) {
      msg = `Do you want to <b>ARM ${toArm.length}</b> and <b>DISARM ${toDisarm.length}</b> partition${totalChanges > 1 ? 's' : ''}?`;
    } else if (toArm.length > 0) {
      msg = `Do you want to set <b>${toArm.length}</b> partition${toArm.length > 1 ? 's' : ''} to <b>ARM</b>?`;
    } else if (toDisarm.length > 0) {
      msg = `Do you want to set <b>${toDisarm.length}</b> partition${toDisarm.length > 1 ? 's' : ''} to <b>DISARM</b>?`;
    }

    this.confirmDialog('Confirm', msg, 'warning', 'orange');
    this.m_warning.data['submit_func'] = () => {
      this.applyPartitionChanges(toArm, toDisarm);
    };
  }

  async applyPartitionChanges(toArm, toDisarm) {
    const allIds = [...toArm, ...toDisarm];
    allIds.forEach(id => this.syncingPartitionIds.add(id));

    const promises = [];

    if (toArm.length > 0) {
      promises.push(this.updatePartitionArmingStatus(toArm, 1));
    }

    if (toDisarm.length > 0) {
      promises.push(this.updatePartitionArmingStatus(toDisarm, 2));
    }

    try {
      await Promise.all(promises);
      this.partitionUpdated.emit({toArm, toDisarm});
      this.m_warning.close();
      this.modal.close();
    } catch (err) {
      allIds.forEach(id => this.syncingPartitionIds.delete(id));
      this.m_warning.close();
      setTimeout(() => {
        this.errorDialog(err, 'Update Partition Arming');
      }, 200);
    }
  }

  updatePartitionArmingStatus(partitionIds: number[], armingAction: number): Promise<any> {
    const dealerId = this.me.dealer_id;
    const siteId = this.site.site_id;
    return lastValueFrom(this.sitesService.updateSitePartitionArming(dealerId, siteId, partitionIds, armingAction));
  }

  closeModal() {
    this.backToStep1();
    this.modal.close();
  }

  confirmDialog(header= '', msg= '', icon= 'done',color='green') {
    this.m_warning.data = {
      header: header,
      contents: `
        <p>${msg}</b></p>
      `,
      submit_btn: 'OK',
      submit_class: ['button-primary'],
      icon : icon,
      isConfirm : true,
      color: color

    };

    this.m_warning.data['submit_func'] = () => {
    };

    this.m_warning.open();
  }

  errorDialog(err , title) {
    let msg = 'failed.';
    if (err.error) {
      if (err.error.message) {
        msg = err.error.message;
      }
    }
    if (err._body) {
      msg = JSON.parse(err._body).message;
    }
    setTimeout(() => {
      this.confirmDialog(title, msg, 'warning', 'orange');
    }, 200);
  }
}
